/**
 * Plan del alta masiva de asignaciones de reloj ("Agregar Varias").
 *
 * Sin React ni axios a propósito: así se puede revisar con un `node script.js`,
 * sin levantar la app.
 *
 * Decide, para cada par (reloj, empleado) elegido en el modal, qué se le pide a
 * `importarAsignacionesRelojes`: un alta (`crear`), volver a prender una
 * asignación deshabilitada (`habilitar`, que viaja en `editar`), o nada. Las
 * reglas son las de `validarFila` del importador Excel; el backend las vuelve a
 * revisar contra la BD de todos modos, esto es para que la vista previa no
 * prometa algo que el servidor va a rechazar.
 */

import {
  claveSerieNumero,
  nombreEmpleado,
  texto,
} from "../../../../Components/importarAsignaciones.utils";
import { etiquetaReloj } from "../ImportarAsignacionRelojes/plantillaAsignacionRelojes";

export const MODO_PIN_SIGUIENTE = "siguiente";
export const MODO_PIN_CODIGO = "codigo";

// Mismo tamaño de bloque que el importador: cada alta encola varias AccionReloj
// y lee al empleado completo (las plantillas biométricas pesan).
export const TAMANO_BLOQUE = 25;

// El 1 es el administrador en los relojes: por omisión no se le entrega a nadie.
export const PINES_OMITIDOS_DEFAULT = "1";

const MAXIMO_PINES_OMITIDOS = 1000;

/**
 * "1, 5, 10-20" → `{ pines: ["1","5","10",...,"20"] }`, o `{ error }` si algo no se
 * entiende. Separadores: coma, punto y coma o espacio. Vacío = no se omite nada.
 */
export const parsearPinesOmitidos = (valor) => {
  const pines = new Set();
  const partes = String(valor ?? "")
    // "10 - 20" es un rango, no tres partes.
    .replace(/\s*-\s*/g, "-")
    .split(/[\s,;]+/)
    .map((parte) => parte.trim())
    .filter(Boolean);

  for (const parte of partes) {
    const rango = parte.match(/^(\d+)-(\d+)$/);
    if (rango) {
      const desde = Number(rango[1]);
      const hasta = Number(rango[2]);
      if (desde > hasta) return { error: `el rango "${parte}" está al revés` };
      if (hasta - desde + 1 + pines.size > MAXIMO_PINES_OMITIDOS) {
        return { error: `son demasiados números (máximo ${MAXIMO_PINES_OMITIDOS})` };
      }
      for (let n = desde; n <= hasta; n += 1) pines.add(String(n));
      continue;
    }
    if (!/^\d+$/.test(parte)) return { error: `"${parte}" no es un número ni un rango (ej. 10-20)` };
    pines.add(String(Number(parte)));
    if (pines.size > MAXIMO_PINES_OMITIDOS) {
      return { error: `son demasiados números (máximo ${MAXIMO_PINES_OMITIDOS})` };
    }
  }

  return { pines: [...pines] };
};

const familiaDe = (catalogos, relojId) =>
  catalogos.familiaPorRelojId.get(String(relojId)) || new Set([String(relojId)]);

const claveFamilia = (familia) => [...familia].sort().join(",");

const asignacionesDelEmpleadoEnFamilia = (catalogos, empleadoId, familia) =>
  (catalogos.porEmpleado.get(String(empleadoId)) || []).filter((a) =>
    familia.has(String(a.relojChecadorId)),
  );

// El código es el número de empleado real (el de NumEmpleado en el CFDI); el
// `numero` es sólo el consecutivo interno EMPn.
const codigoDe = (empleado) =>
  empleado?.codigo === undefined || empleado?.codigo === null ? "" : texto(empleado.codigo);

/**
 * Asignaciones ACTIVAS del empleado en los relojes dados (contando la familia
 * física de cada uno): `[{ relojId, relojNombre, relojNombreCorto, pin }]`. Es lo
 * que pinta la columna "Ya asignado en" de la tabla de selección.
 */
export const activasEnRelojes = (catalogos, empleadoId, relojIds) => {
  const resultado = [];
  for (const relojId of relojIds) {
    const familia = familiaDe(catalogos, relojId);
    const activa = asignacionesDelEmpleadoEnFamilia(catalogos, empleadoId, familia).find(
      (a) => Number(a.estado) === 1,
    );
    if (activa) {
      const reloj = catalogos.relojPorId.get(String(relojId));
      resultado.push({
        relojId: String(relojId),
        relojNombre: etiquetaReloj(reloj),
        relojNombreCorto: texto(reloj?.nombre) || etiquetaReloj(reloj),
        pin: texto(activa.usuarioRelojChecador),
      });
    }
  }
  return resultado;
};

/**
 * Un renglón por par (reloj, empleado), con `operacion`:
 *   "crear"      → alta nueva; `usuarioRelojChecador` vacío = siguiente libre
 *   "habilitar"  → vuelve a prender `idExistente` con su mismo usuario
 *   "sinCambios" → ya está activo en ese reloj; no se manda
 *   "error"      → no se manda; `motivo` dice por qué
 *
 * `pinesOmitidos`: números que no se le dan a nadie. El "siguiente libre" se los
 * salta (eso lo hace el backend con la misma lista) y aquí no se re-habilita una
 * deshabilitada que traiga uno ni se acepta un código que caiga en ellos.
 */
export const planearAsignaciones = ({
  catalogos,
  empleadoIds,
  relojIds,
  modoPin,
  pinesOmitidos = [],
}) => {
  const plan = [];
  const omitidos = new Set(pinesOmitidos.map((pin) => texto(pin)));

  // Usuarios apartados por este mismo lote, por familia: dos empleados con el
  // mismo código no pueden quedarse con el mismo usuario en el mismo reloj.
  const apartadosPorFamilia = new Map();
  // Primer reloj elegido de cada familia: si se eligen dos registros del mismo
  // reloj físico, el segundo sale como error en vez de intentar el alta dos veces.
  const primerRelojDeFamilia = new Map();

  for (const relojId of relojIds) {
    const reloj = catalogos.relojPorId.get(String(relojId));
    const relojNombre = etiquetaReloj(reloj);
    const familia = familiaDe(catalogos, relojId);
    const clave = claveFamilia(familia);

    const relojRepetido = primerRelojDeFamilia.get(clave);
    if (!relojRepetido) primerRelojDeFamilia.set(clave, relojNombre);

    if (!apartadosPorFamilia.has(clave)) apartadosPorFamilia.set(clave, new Map());
    const apartados = apartadosPorFamilia.get(clave);

    // Quién ocupa cada usuario en toda la familia. Sólo las activas: una
    // deshabilitada ya no tiene usuario en el dispositivo.
    const ocupantePorPin = new Map();
    for (const id of familia) {
      for (const activa of catalogos.activasPorReloj.get(id) || []) {
        ocupantePorPin.set(texto(activa.usuarioRelojChecador), activa);
      }
    }

    const nombreDelOcupante = (ocupante) =>
      nombreEmpleado(catalogos.empleadoPorId.get(String(ocupante.empleadoId))) ||
      "otra asignación";

    for (const empleadoId of empleadoIds) {
      const empleado = catalogos.empleadoPorId.get(String(empleadoId));
      if (!empleado) continue;

      const base = {
        key: `${relojId}|${empleadoId}`,
        empleadoId: String(empleado._id),
        relojChecadorId: String(relojId),
        empleadoNumero: claveSerieNumero(empleado),
        empleadoCodigo: codigoDe(empleado),
        empleadoNombre: nombreEmpleado(empleado),
        relojChecadorNombre: relojNombre,
        usuarioRelojChecador: "",
      };

      if (relojRepetido) {
        plan.push({
          ...base,
          operacion: "error",
          motivo: `es el mismo reloj físico que "${relojRepetido}"`,
        });
        continue;
      }

      const delEmpleado = asignacionesDelEmpleadoEnFamilia(catalogos, empleado._id, familia);

      const activa = delEmpleado.find((a) => Number(a.estado) === 1);
      if (activa) {
        plan.push({
          ...base,
          operacion: "sinCambios",
          usuarioRelojChecador: texto(activa.usuarioRelojChecador),
          motivo: `ya está asignado con el usuario ${texto(activa.usuarioRelojChecador)}`,
        });
        continue;
      }

      // La más reciente primero: si hay varias deshabilitadas, se prende esa.
      const deshabilitadas = delEmpleado
        .filter((a) => Number(a.estado) === 2)
        .sort((a, b) => (Number(b.numero) || 0) - (Number(a.numero) || 0));

      if (modoPin === MODO_PIN_CODIGO) {
        const pin = codigoDe(empleado);

        if (!pin) {
          plan.push({ ...base, operacion: "error", motivo: "el empleado no tiene código capturado" });
          continue;
        }

        if (omitidos.has(pin)) {
          plan.push({
            ...base,
            usuarioRelojChecador: pin,
            operacion: "error",
            motivo: `el usuario ${pin} está en los números omitidos`,
          });
          continue;
        }

        const ocupante = ocupantePorPin.get(pin);
        if (ocupante) {
          plan.push({
            ...base,
            usuarioRelojChecador: pin,
            operacion: "error",
            motivo: `el usuario ${pin} ya lo tiene ${nombreDelOcupante(ocupante)} en ese reloj`,
          });
          continue;
        }

        if (apartados.has(pin)) {
          plan.push({
            ...base,
            usuarioRelojChecador: pin,
            operacion: "error",
            motivo: `el usuario ${pin} también le toca a ${apartados.get(pin)} en esta misma selección (tienen el mismo código)`,
          });
          continue;
        }

        apartados.set(pin, base.empleadoNombre);

        const misma = deshabilitadas.find((a) => texto(a.usuarioRelojChecador) === pin);
        if (misma) {
          plan.push({
            ...base,
            usuarioRelojChecador: pin,
            operacion: "habilitar",
            idExistente: String(misma._id),
          });
          continue;
        }

        plan.push({ ...base, usuarioRelojChecador: pin, operacion: "crear" });
        continue;
      }

      // Siguiente libre: si tiene una deshabilitada cuyo usuario nadie ocupa, se
      // vuelve a prender esa (conserva su número); si no, alta nueva y el backend
      // le pone el siguiente libre de la familia.
      const candidata = deshabilitadas.find((a) => {
        const pin = texto(a.usuarioRelojChecador);
        return pin && !omitidos.has(pin) && !ocupantePorPin.has(pin) && !apartados.has(pin);
      });

      if (candidata) {
        const pin = texto(candidata.usuarioRelojChecador);
        apartados.set(pin, base.empleadoNombre);
        plan.push({
          ...base,
          usuarioRelojChecador: pin,
          operacion: "habilitar",
          idExistente: String(candidata._id),
        });
        continue;
      }

      plan.push({ ...base, operacion: "crear" });
    }
  }

  return plan;
};

export const resumirPlan = (plan) => {
  const resumen = { crear: 0, habilitar: 0, sinCambios: 0, error: 0 };
  for (const fila of plan) {
    if (resumen[fila.operacion] !== undefined) resumen[fila.operacion] += 1;
  }
  return resumen;
};

// Sólo lo que lee importarAsignacionesRelojes. Los nombres viajan porque con
// ellos arma la `etiqueta` de cada resultado.
const paraEnviar = (fila) => ({
  empleadoId: fila.empleadoId,
  relojChecadorId: fila.relojChecadorId,
  usuarioRelojChecador: fila.usuarioRelojChecador,
  empleadoNombre: fila.empleadoNombre,
  relojChecadorNombre: fila.relojChecadorNombre,
  ...(fila.operacion === "habilitar"
    ? { operacion: "habilitar", idExistente: fila.idExistente }
    : {}),
});

/**
 * Bloques `{ crear, editar }` para mandar en serie.
 *
 * Las re-habilitaciones van PRIMERO y en bloques propios, nunca mezcladas con
 * altas: dentro de una misma petición el backend procesa `crear` antes que
 * `editar`, y el "siguiente libre" sólo ve las activas. Si una deshabilitada
 * trae un usuario más alto que el máximo de las activas, un alta del mismo bloque
 * podría quedarse ese número y la re-habilitación fallaría por duplicado.
 * Prendiéndolas antes, su usuario ya cuenta para el máximo.
 */
export const armarBloques = (plan, tamano = TAMANO_BLOQUE) => {
  const editar = plan.filter((f) => f.operacion === "habilitar").map(paraEnviar);
  const crear = plan.filter((f) => f.operacion === "crear").map(paraEnviar);

  const bloques = [];
  for (let i = 0; i < editar.length; i += tamano) {
    bloques.push({ crear: [], editar: editar.slice(i, i + tamano) });
  }
  for (let i = 0; i < crear.length; i += tamano) {
    bloques.push({ crear: crear.slice(i, i + tamano), editar: [] });
  }
  return bloques;
};
